/**
 * Audit Log API Service
 *
 * Type-safe audit log access using openapi-fetch typed client.
 * Types derived from audit-trail.openapi.yaml -- no manual interfaces.
 *
 * Endpoints consumed (via audit-trail spec):
 *   GET    /audit/logs              - Query with filters
 *   GET    /audit/logs/{logId}      - Single entry
 *   POST   /audit/logs/{logId}/verify - Integrity verification
 */

import { auditTrailClient } from '../client';
import type { components } from '@aaimplatform/institutional-types/audit-trail';
import type { AuditLogging } from '@aaimplatform/institutional-types';

// Re-export generated types for consumers that import from this module
export type AuditLogEntry = components['schemas']['AuditLogEntryResponse'];
export type AuditLogQueryResponse = components['schemas']['AuditLogQueryResponse'];
export type AuditLogEntryResponse = components['schemas']['AuditLogEntryResponse'];
export type AuditLogIntegrityResponse = components['schemas']['AuditLogIntegrityResponse'];
export type AuditActionType = components['schemas']['AuditActionType'];

export type AuditStatistics = components['schemas']['AuditLogStatistics'];
export type AuditIntegrityResponse = AuditLogIntegrityResponse;

export type AuditLogFilterParams = AuditLogging.components['schemas']['AuditLogFilterParams'];
/**
 * Audit Log API service singleton.
 */
export const auditApi = {
  /**
   * List audit logs with optional filtering, sorting, and pagination.
   */
  async list(params: AuditLogFilterParams = {}): Promise<AuditLogQueryResponse> {
    const { data, error } = await auditTrailClient.GET('/audit/logs', {
      params: {
        query: {
          tenantId: params.tenantId,
          userId: params.userId,
          action: params.action as AuditActionType | undefined,
          resourceType: params.resourceType,
          resourceId: params.resourceId,
          startDate: params.startDate,
          endDate: params.endDate,
          limit: params.limit,
          offset: params.offset,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
        },
      },
    });
    if (error) throw new Error('Failed to fetch audit logs');
    return data;
  },

  /**
   * Get a single audit log entry by ID.
   */
  async getById(logId: string): Promise<AuditLogEntryResponse> {
    const { data, error } = await auditTrailClient.GET('/audit/logs/{logId}', {
      params: { path: { logId } },
    });
    if (error) throw new Error('Failed to fetch audit log entry');
    return data;
  },

  /**
   * Export audit logs as CSV. Returns the download URL.
   * Note: This is a navigation URL, not an API call through openapi-fetch.
   */
  getExportUrl(params: AuditLogFilterParams = {}): string {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.set(key, String(value));
      }
    }
    const qs = searchParams.toString();
    return `/api/admin/audit/logs/export${qs ? `?${qs}` : ''}`;
  },

  /**
   * Get aggregate audit log statistics.
   */
  async getStatistics(tenantId?: string): Promise<AuditStatistics> {
    const { data, error } = await auditTrailClient.GET('/audit/logs/statistics', {
      params: { query: { tenantId } },
    });
    if (error) throw new Error('Failed to fetch audit statistics');
    return data;
  },

  /**
   * Verify the integrity of a specific audit log entry.
   */
  async verifyIntegrity(logId: string): Promise<AuditLogIntegrityResponse> {
    const { data, error } = await auditTrailClient.POST('/audit/logs/{logId}/verify', {
      params: { path: { logId } },
    });
    if (error) throw new Error('Failed to verify audit log integrity');
    return data;
  },
};
